import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Badge } from "@/components/ui/PageHeader";
import { submitClaim, myClaims, type ClaimEntityType } from "@/api/claims";

/**
 * "Claim this page" — drops onto a provider or institution detail page.
 * A real owner asserts ownership; an admin reviews before it transfers.
 * Shows the current claim status if one already exists.
 */
export default function ClaimButton({
  entityType,
  entityId,
  label,
}: {
  entityType: ClaimEntityType;
  entityId: string;
  /** Friendly noun for the copy (e.g. "organizer"); defaults to entityType. */
  label?: string;
}) {
  const noun = label ?? entityType;
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [evidence, setEvidence] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data, refetch } = useQuery({ queryKey: ["my-claims"], queryFn: myClaims });
  const existing = (data?.data?.claims ?? []).find(
    (c) => c.entityType === entityType && c.entityId === entityId,
  );

  async function submit() {
    setBusy(true);
    setMsg(null);
    try {
      await submitClaim({
        entityType,
        entityId,
        claimantNote: note || undefined,
        evidenceUrls: evidence.trim() ? evidence.split(",").map((s) => s.trim()).filter(Boolean) : [],
      });
      setMsg("Claim submitted — an admin will review it");
      setOpen(false);
      refetch();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : "Failed to submit");
    } finally {
      setBusy(false);
    }
  }

  if (existing) {
    const variant = existing.status === "approved" ? "green" : existing.status === "rejected" ? "gray" : "yellow";
    return (
      <div className="text-sm text-gray-600 flex items-center gap-2">
        Your claim on this {noun}: <Badge variant={variant}>{existing.status}</Badge>
        {existing.status === "rejected" && existing.reviewNote && (
          <span className="text-xs text-gray-400">({existing.reviewNote})</span>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {!open ? (
        <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
          Claim this {noun}
        </Button>
      ) : (
        <div className="border border-gray-200 rounded-xl p-3 space-y-2 bg-gray-50">
          <p className="text-xs text-gray-500">
            Claim ownership if you run this {noun}. An admin reviews before control transfers.
          </p>
          <textarea
            className="input"
            rows={2}
            placeholder="How are you connected to this page? (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <input
            className="input"
            placeholder="Evidence URLs, comma-separated (optional)"
            value={evidence}
            onChange={(e) => setEvidence(e.target.value)}
          />
          <div className="flex gap-2">
            <Button size="sm" isLoading={busy} onClick={submit}>
              Submit claim
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      {msg && <p className="text-sm text-gray-600">{msg}</p>}
    </div>
  );
}
